/**
 * StaleBanner — Phase 6a.18a per Spec §21.8.0.
 *
 * Amber banner rendered above the result tab strip on
 * `EngagementResultsPage` when an engagement's stored `lastResult` is no
 * longer consistent with its current inputs (the auditor edited a
 * contract or engagement-level field after the last successful
 * calculation). Visually distinct from the verbatim-Big4 WarningsBanner
 * (Phase 6c.2): this one is single-purpose, non-dismissible, and owns
 * its own "Yeniden Hesapla" CTA so the auditor can clear the staleness
 * in one click.
 *
 * The banner is rendered ONLY when both `engagement.isStale === true`
 * AND `engagement.lastResult` is truthy — there is no point flagging
 * staleness on a draft engagement that was never calculated.
 */

import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

export interface StaleBannerProps {
  /**
   * Fires when the auditor clicks "Yeniden Hesapla". Callers wire this
   * to the engagement-level recalculate flow (typically navigating to
   * the contract list where the Hesapla button lives).
   */
  onRecalculate: () => void;
  /**
   * When `true`, the button is disabled and shows a "Hesaplanıyor..."
   * label. Used while the backend POST is in flight to prevent a
   * double-submit.
   */
  recalculating?: boolean;
}

/**
 * Phase BB5-1 — compact inline alert. Single row, `max-h-12` (48px),
 * left accent + tinted background, recalculate CTA on the right.
 * The previous two-line Card layout was eating ~80px of vertical
 * real-estate above the tab strip; auditors with both banners visible
 * had to scroll to reach the content.
 */
export function StaleBanner({
  onRecalculate,
  recalculating,
}: StaleBannerProps) {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-3 rounded-md border-l-4 border-yellow-500 bg-yellow-500/10 px-4 py-2 mb-1"
      data-testid="stale-banner"
    >
      <AlertTriangle
        className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0"
        aria-hidden="true"
      />
      <p
        className="flex-1 min-w-0 text-sm font-medium text-foreground truncate"
        data-testid="stale-banner-title"
      >
        {t("staleBanner.compact")}
      </p>
      <Button
        size="sm"
        onClick={onRecalculate}
        disabled={recalculating}
        data-testid="stale-banner-recalculate"
      >
        {recalculating
          ? t("staleBanner.recalculating")
          : t("staleBanner.recalculate")}
      </Button>
    </div>
  );
}
